import { useMemo } from 'react';
import { DevicePhoneMobileIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../AuthContext';
import useFieldStaffHome from './fieldStaffHome/useFieldStaffHome';
import {
  todayVisits, needsItems, payPeriodSummary, timeOffSummary, messageSummary, clientRows,
} from './fieldStaffHome/fieldStaffHomeModel';
import {
  TodayCard, NeedsCard, PayPeriodCard, ClientsCard, TimeOffCard, MessagesCard,
} from './fieldStaffHome/FieldStaffHomeCards';
import PlansToReadList from '../components/program245d/planAcknowledgments/PlansToReadList';

/**
 * Web home for field staff (PCAs, caregivers, drivers), approved mockup
 * 2026-10-07: their own day, what needs them, this pay period, their clients,
 * time off and messages. Every request is self-scoped on the server, so nothing
 * here shows other clients or other staff. Their visits, clock-in and notes
 * stay in the phone app. Every page outside fieldStaffWebAccess's list renders
 * this instead.
 */

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function greeting(now, firstName) {
  const hour = now.getHours();
  const part = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';
  return firstName ? `Good ${part}, ${firstName}` : `Good ${part}`;
}

// Worst status of the parts a card draws from: any error, else any still loading.
const combined = (...parts) => (parts.some((p) => p.status === 'error') ? 'error'
  : parts.some((p) => p.status === 'loading') ? 'loading' : 'ok');

export default function FieldStaffWebHome() {
  const { user } = useAuth();
  const { parts, timesheet, period, plans } = useFieldStaffHome();
  const now = new Date();

  const today = useMemo(() => todayVisits(parts.today.data), [parts.today.data]);
  const needs = useMemo(() => needsItems({
    unsigned: parts.unsigned.data?.count || 0,
    flagged: parts.flagged.data?.count || 0,
    forms: parts.forms.data || 0,
    unsignedVisits: parts.unsigned.data?.items,
    flaggedVisits: parts.flagged.data?.items,
    pendingForms: parts.pendingForms.data,
  }), [parts.unsigned.data, parts.flagged.data, parts.forms.data, parts.pendingForms.data]);
  const pay = useMemo(() => payPeriodSummary(period, timesheet.data), [period, timesheet.data]);
  const off = useMemo(() => timeOffSummary(parts.balances.data, parts.requests.data), [parts.balances.data, parts.requests.data]);
  const clients = useMemo(() => clientRows(parts.clients.data, today), [parts.clients.data, today]);
  const messages = messageSummary(parts.messages.data, user?.id || user?.userId);

  // A section refused to everyone in "Needs you" (e.g. a driver) shows nothing,
  // not an error: only count parts that are offered to this person.
  const needsParts = [parts.unsigned, parts.flagged, parts.forms].filter((p) => p.status !== 'hidden');

  return (
    <div className="space-y-5 p-4 sm:p-6">
      <section className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900 dark:text-white">{greeting(now, user?.firstName || user?.first_name)}</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            {`${WEEKDAYS[now.getDay()]}, ${MONTHS[now.getMonth()]} ${now.getDate()} · Your clients, visits and visit notes are in the AraSync app.`}
          </p>
        </div>
        {today?.next && (
          <div className="flex items-center gap-3 rounded-xl border border-solid border-gray-200 bg-white px-4 py-3 shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <DevicePhoneMobileIcon className="h-6 w-6 text-teal-600 dark:text-teal-400" aria-hidden="true" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Next visit</p>
              <p className="font-bold text-gray-900 dark:text-white">{`${today.next.client} · ${today.next.time}`}</p>
            </div>
          </div>
        )}
      </section>

      <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-2">
        <TodayCard status={parts.today.status} today={today} />
        <PlansToReadList plans={plans} variant="card" />
        {needsParts.length > 0 && <NeedsCard status={combined(...needsParts)} items={needs} />}
        <PayPeriodCard status={timesheet.status} summary={pay} />
        <ClientsCard status={parts.clients.status} clients={clients} />
        <TimeOffCard
          status={parts.balances.status === 'hidden' && parts.requests.status === 'hidden' ? 'hidden' : combined(parts.balances, parts.requests)}
          summary={off}
        />
        <MessagesCard status={parts.messages.status} summary={messages} />
      </div>
    </div>
  );
}
